/* @flow */

import * as React from 'react';
import glamorous from 'glamorous';
import moment from 'moment';
import { first } from 'lodash/fp';
import download from 'downloadjs';
import MobileDetect from 'mobile-detect';
import { mapErrorMessages } from 'app/utils/challengeUtils';
import Responsive from 'app/components/responsive/Responsive';
import withNotificationCallback from 'app/components/notifications/withNotificationCallback';
import Button from 'app/components/commons/Button';
import Icon from 'app/components/commons/Icon';
import { getMyHistoryEntries } from 'app/api/challenges';

type Props = {
  transmitNotification: Object => void
};

type State = {
  minDate: string,
  maxDate: string,
  startDate: string,
  endDate: string
};

const FilterBar = glamorous.div(({ theme }) => ({
  marginBottom: theme.spacing.tiny,
  padding: theme.spacing.xsmall,
  backgroundColor: theme.color.basis6,
  color: theme.color.basis1,
  overflow: 'hidden',
  display: 'flex',
  justifyContent: 'flex-end',
  [theme.mediaQuery.desktopAndUp]: {
    fontSize: '0.8rem'
  },
  [theme.mediaQuery.mobileOnly]: {
    justifyContent: 'space-between',
    alignItems: 'center'
  }
}));

const ExportIcon = glamorous(Icon)({
  fontSize: '1.2em',
  marginTop: '-5px'
});

const StyledExportHistory = glamorous(Button)({
  textTransform: 'uppercase'
});

const DateField = glamorous.div(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  borderRadius: theme.borderRadius.small,
  border: `1px solid ${theme.color.basis4}`,
  paddingLeft: theme.spacing.small,
  paddingRight: theme.spacing.small,
  marginRight: theme.spacing.tiny,
  [theme.mediaQuery.mobileOnly]: {
    paddingLeft: theme.spacing.tiny,
    paddingRight: theme.spacing.tiny
  }
}));

const Label = glamorous.div(({ theme }) => ({
  paddingRight: theme.spacing.tiny,
  textTransform: 'uppercase',
  [theme.mediaQuery.mobile]: {
    display: 'none'
  }
}));

const DateInput = glamorous.input(({ theme }) => ({
  border: 'none',
  fontSize: '0.9rem',
  backgroundColor: theme.color.basis6,
  minWidth: 0,
  flexShrink: 1,
  flexGrow: 1,
  [theme.mediaQuery.tabletPortraitAndUp]: {
    width: '120px'
  }
}));

const ExportLabel = glamorous.span(({ theme }) => ({
  paddingRight: theme.spacing.tiny,
  [theme.mediaQuery.mobileSmallOnly]: {
    display: 'none'
  }
}));

const Fieldset = glamorous.fieldset(({ theme }) => ({
  display: 'flex',
  flexShrink: 1,
  flexGrow: 1,
  minWidth: 0,
  borderRadius: theme.borderRadius.small,
  paddingBlockStart: theme.spacing.tiny,
  paddingBlockEnd: theme.spacing.tiny
}));

class AccountHistoryFilterBar extends React.Component<Props, State> {
  props: Props;

  state: State = {
    minDate: '2018-05-30',
    maxDate: moment().format('YYYY-MM-DD'),
    startDate: moment()
      .subtract(6, 'months')
      .format('YYYY-MM-DD'),
    endDate: moment().format('YYYY-MM-DD')
  };

  handleClick = () => {
    const { startDate, endDate, minDate } = this.state;
    const { transmitNotification } = this.props;

    if (!startDate || !endDate) {
      transmitNotification({
        message: 'Please select start date and end date.',
        type: 'danger'
      });
      return;
    }

    if (moment(startDate) > moment(endDate)) {
      transmitNotification({
        message: 'Start date should be earlier than end date.',
        type: 'danger'
      });
      return;
    }

    if (moment(startDate) > moment() || moment(endDate) > moment()) {
      transmitNotification({
        message: 'Date cannot be in the future.',
        type: 'danger'
      });
      return;
    }

    if (moment(startDate).isBefore(moment(minDate))) {
      transmitNotification({
        message: `You can only export history from ${moment(minDate).format('Do MMM YYYY')}`,
        type: 'danger'
      });
      return;
    }

    let method = 'email';

    try {
      const md = new MobileDetect(window.navigator.userAgent);
      method = md.mobile() ? 'email' : 'download';
    } catch (err) {
      // do nothing
    }

    getMyHistoryEntries({
      method,
      from: encodeURIComponent(moment(startDate).format('YYYY-MM-DDT00:00:00Z')),
      to: encodeURIComponent(moment(endDate).format('YYYY-MM-DDT23:59:59Z'))
    })
      .then(blob => {
        if (method === 'email') {
          transmitNotification({
            message: "We've emailed the file to you, please check your email.",
            type: 'success'
          });
        } else if (method === 'download') {
          download(
            blob,
            `history_${moment(startDate).format('YYYY-MM-DD')}_to_${moment(endDate).format(
              'YYYY-MM-DD'
            )}.csv`,
            'text/csv'
          );
        }
      })
      .catch(({ errors }) => {
        const error = first(mapErrorMessages(errors));
        transmitNotification({
          message: error || 'Failed to export history.',
          type: 'danger'
        });
      });
  };

  setFormField = ({ target }) => {
    this.setState({
      [target.name]: target.value
    });
  };

  render() {
    const { minDate, maxDate, startDate, endDate } = this.state;

    return (
      <FilterBar>
        <Responsive mobileLandscape tabletLandscape tabletPortrait desktop desktopLarge>
          <DateField>
            <Label>From:</Label>
            <DateInput
              type="date"
              name="startDate"
              min={minDate}
              max={maxDate}
              value={startDate}
              onChange={this.setFormField}
            />
          </DateField>
        </Responsive>
        <Responsive mobilePortrait>
          <Fieldset>
            <legend>From</legend>
            <DateInput
              type="date"
              name="startDate"
              min={minDate}
              max={maxDate}
              value={startDate}
              onChange={this.setFormField}
            />
          </Fieldset>
        </Responsive>
        <Responsive mobileLandscape tabletLandscape tabletPortrait desktop desktopLarge>
          <DateField>
            <Label>To:</Label>
            <DateInput
              type="date"
              name="endDate"
              min={minDate}
              max={maxDate}
              value={endDate}
              onChange={this.setFormField}
            />
          </DateField>
        </Responsive>
        <Responsive mobilePortrait>
          <Fieldset>
            <legend>To</legend>
            <DateInput
              type="date"
              name="endDate"
              min={minDate}
              max={maxDate}
              value={endDate}
              onChange={this.setFormField}
            />
          </Fieldset>
        </Responsive>
        <StyledExportHistory kind="info" onClick={this.handleClick}>
          <ExportLabel>Export</ExportLabel>
          <ExportIcon type="icon-export" />
        </StyledExportHistory>
      </FilterBar>
    );
  }
}

export default withNotificationCallback(AccountHistoryFilterBar);
